// The standalone Recipe dialog, for recipe-only flavors (no product). A
// product's recipe is edited on the Recipe tab of the product dialog
// (ProductFormDialog); both use the editor in recipeEditor.tsx.

import { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { UtensilsCrossed } from 'lucide-react'
import { usePublishLabelProducts } from '../../hooks/backend/inventoryPage'
import { publishLabels } from '../../utils/publishLabels'
import { toast } from '../../lib/shadcn/sonner'
import type { Product } from './ProductTable'
import {
  RecipeEditorFields,
  RecipeSaveConfirm,
  recipeDescription,
  useRecipeEditor,
} from './recipeEditor'

interface RecipeDialogProps {
  product: Product | null
  /**
   * A recipe-only flavor (no product has this Label Name, e.g. the green apple
   * popcorn in Caramel Apple), opened without a product. Ignored when
   * `product` is set.
   */
  flavorName?: string | null
  /** Start a new recipe-only flavor: the dialog asks for its name. */
  createNew?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: () => void
}

export default function RecipeDialog({
  product,
  flavorName,
  createNew = false,
  open,
  onOpenChange,
  onSaved,
}: RecipeDialogProps) {
  const editor = useRecipeEditor({ product, flavorName: flavorName ?? null, createNew, active: open })
  const publishLabelProducts = usePublishLabelProducts()
  const { hasLabel, labelName, confirmOpen, setConfirmOpen, saving, recipeLoading } = editor

  useEffect(() => {
    if (!open && !confirmOpen) {
      const frame = requestAnimationFrame(() => {
        document.body.style.pointerEvents = ''
      })
      return () => cancelAnimationFrame(frame)
    }
  }, [open, confirmOpen])

  const doSave = async (apply: boolean, changedOnly = false) => {
    const res = await editor.save(apply, changedOnly)
    if (!res) return
    onOpenChange(false)
    requestAnimationFrame(() => {
      document.body.style.pointerEvents = ''
      onSaved?.()
    })
    void publishLabels(res.publishLabels ?? [], params => publishLabelProducts.trigger(params))
  }

  const handleSaveClick = () => {
    const problem = editor.checkSave()
    if (problem) {
      toast.error(problem)
      return
    }
    const mode = editor.saveMode()
    if (mode === 'plain') void doSave(false)
    else if (mode === 'changedOnly') void doSave(true, true)
    else setConfirmOpen(true)
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={nextOpen => {
          if (!nextOpen) setConfirmOpen(false)
          onOpenChange(nextOpen)
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UtensilsCrossed className="h-4 w-4" /> Recipe
            </DialogTitle>
            <DialogDescription>{recipeDescription(editor)}</DialogDescription>
          </DialogHeader>

          <RecipeEditorFields editor={editor} saveLabel={createNew ? 'Add flavor' : 'Save recipe'} />

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {hasLabel ? 'Cancel' : 'Close'}
            </Button>
            {hasLabel ? (
              <Button onClick={handleSaveClick} disabled={saving || recipeLoading || !labelName}>
                {saving ? 'Saving…' : createNew ? 'Add flavor' : 'Save recipe'}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RecipeSaveConfirm editor={editor} saving={saving} onSave={apply => void doSave(apply)} />
    </>
  )
}
